/**
 * Payment Method Update Section JavaScript
 * Handles payment method selection and updates for subscription contracts
 */

const NS = 'payment-method-update';

class PaymentMethodUpdate extends HTMLElement {
  selectedPaymentMethodId = null;
  customerId = null;
  subscriptionId = null;
  apiBaseUrl = null;
  activeSubscriptions = [];
  nonGiftSubscriptions = [];

  constructor() {
    super();
    this.getData();
    this.setupListeners();
    this.init();
  }

  /**
   * Get liquid data from component's dataset
   */
  getData() {
    this.customerId = this.dataset.customerId;
    this.apiBaseUrl = this.dataset.apiBaseUrl;
    this.helpMessage = this.dataset.helpMessage;
  }

  /**
   * event listeners
   */
  setupListeners() {
    const updateButton = this.querySelector('#update-payment-method-button');
    if (updateButton) {
      updateButton.addEventListener('click', () => {
        this.updatePaymentMethod();
      });
    }
  }

  /**
   * Initialize the component
   */
  init() {
    if (this.customerId) {
      this.fetchActiveSubscriptions();
    } else {
      this.showMessage(
        'error-message',
        'Unable to load payment methods. Please ensure you are logged in or contact customer support.',
        true,
        false
      );
    }
  }

  /**
   * Lookup customer's active subscription contracts via app lambda proxy to Appstle API.
   */
  async fetchActiveSubscriptions() {
    if (!this.customerId) {
      return;
    }
    try {
      const res = await fetch('/apps/customer-actions/get_subscription_contracts', {
        method: 'POST',
        body: JSON.stringify({ customerId: this.customerId }),
      });
      const data = await res.json();
      this.activeSubscriptions = data.data;

      this.nonGiftSubscriptions = this.activeSubscriptions.filter(
        (x) => !JSON.parse(x.contractDetailsJSON)[0].sellingPlanName.includes('Gift Subscription')
      );

      if (this.nonGiftSubscriptions.length > 0) {
        if (this.nonGiftSubscriptions.length === 1) {
          this.showMessage(
            'success-message',
            '1 active subscription found. Payment method will be updated for this subscription.',
            false,
            false
          );
        } else {
          this.showMessage(
            'success-message',
            `${this.nonGiftSubscriptions.length} active subscriptions found. Payment method will be updated for all subscriptions.`,
            false,
            false
          );
        }
      } else {
        this.showMessage(
          'success-message',
          'No active subscriptions found. Payment method will be updated at the customer level.',
          false,
          false
        );
      }

      this.loadPaymentMethods();

      this.displaySubscriptionInfo();
    } catch (error) {
      console.error('Error fetching active subscriptions:', error);
      this.loadPaymentMethods();
    }
  }

  /**
   * Display information about which subscriptions will be affected
   */
  displaySubscriptionInfo() {
    if (this.nonGiftSubscriptions.length === 0) return;

    const container = this.querySelector('#payment-methods-list');
    if (!container) return;

    const subscriptionInfo = document.createElement('div');
    subscriptionInfo.className = 'subscription-info';
    subscriptionInfo.innerHTML = `
      <div class="subscription-info-header">
        <h3 class="font-h4">Subscriptions to be updated:</h3>
        <p class="font-body-2">The following ${
          this.nonGiftSubscriptions.length
        } subscription(s) will use the new payment method:</p>
      </div>
      <div class="subscription-list">
        ${this.nonGiftSubscriptions
          .map((sub) => {
            const contractDetails = JSON.parse(sub.contractDetailsJSON)[0];
            return `
            <div class="subscription-item">
              <span class="subscription-name">${contractDetails.title + ' ' + 'Subscription'}</span>
            </div>
          `;
          })
          .join('')}
      </div>
    `;

    container.insertBefore(subscriptionInfo, container.firstChild);

    // Add help message after subscription info
    const helpMessage = document.createElement('div');
    helpMessage.className = 'payment-methods-help';
    helpMessage.innerHTML = `<p class="font-body-3">${this.helpMessage}</p>`;
    container.insertBefore(helpMessage, subscriptionInfo.nextSibling);
  }

  /**
   * Show message to user
   * @param {string} elementId - ID of the message element
   * @param {string} message - Message text to display
   * @param {boolean} isError - Whether this is an error message
   * @param {boolean} autoHide - Whether to auto-hide the message
   */
  showMessage(elementId, message, isError = false, autoHide = true) {
    // Hide all other messages first to ensure only one is shown at a time
    this.hideMessage('loading-message');
    this.hideMessage('error-message');
    this.hideMessage('success-message');

    const element = this.querySelector(`#${elementId}`);
    if (!element) return;

    element.innerHTML = `<span class="font-body-2">${message}</span>`;
    element.style.display = 'block';

    // Set proper CSS class based on message type
    if (isError) {
      element.className = 'message error-message';
    } else if (elementId === 'loading-message') {
      element.className = 'message loading-message';
    } else {
      element.className = 'message success-message';
    }

    // Only auto-hide success messages, keep error messages visible
    if (autoHide && !isError) {
      const timeout = setTimeout(() => {
        if (element) {
          element.style.display = 'none';
        }
      }, 5000);
      element._autoHideTimeout = timeout;
    }
  }

  /**
   * Hide a specific message
   * @param {string} elementId - ID of the message element to hide
   */
  hideMessage(elementId) {
    const element = this.querySelector(`#${elementId}`);
    if (element) {
      element.style.display = 'none';
    }
  }

  /**
   * Update the state of the update button based on selection
   */
  updateButtonState() {
    const updateButton = this.querySelector('#update-payment-method-button');
    if (!updateButton) return;

    const currentPaymentMethod = this.querySelector('.payment-method-card.current-method input[type="radio"]');
    if (currentPaymentMethod && this.selectedPaymentMethodId === currentPaymentMethod.value) {
      updateButton.disabled = true;
      updateButton.title = 'Selected payment method is already the current method';
    } else {
      updateButton.disabled = false;
      updateButton.title = 'Click to update payment method';
    }
  }

  /**
   * Clear the payment method interface after successful update
   */
  clearPaymentMethodInterface() {
    const paymentMethodsList = this.querySelector('#payment-methods-list');
    if (paymentMethodsList) {
      paymentMethodsList.innerHTML = '';
    }

    const updateButton = this.querySelector('#update-payment-method-button');
    if (updateButton) {
      updateButton.style.display = 'none';
    }

    this.hideMessage('loading-message');
    this.hideMessage('error-message');

    // Ensure success message stays visible and doesn't auto-hide
    const successMessage = this.querySelector('#success-message');
    if (successMessage) {
      successMessage.style.display = 'block';
      if (successMessage._autoHideTimeout) {
        clearTimeout(successMessage._autoHideTimeout);
        delete successMessage._autoHideTimeout;
      }
    }
  }

  /**
   * Edit payment method using Shopify's hosted update page or email fallback
   * @param {string} paymentMethodId - The ID of the payment method to edit
   */
  async editPaymentMethod(paymentMethodId) {
    try {
      this.hideMessage('success-message');
      this.hideMessage('error-message');

      this.showMessage('loading-message', 'Checking payment method update options...', false, true);

      const response = await fetch(`${this.apiBaseUrl}/get_payment_method_update_url?customerId=${this.customerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerPaymentMethodId: paymentMethodId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.data && result.data.updateUrl) {
        this.showMessage('loading-message', 'Redirecting to payment method update page...', false, true);
        window.location.href = result.data.updateUrl;
        return;
      }

      if (result.data && result.data.method === 'email_sent') {
        this.showMessage(
          'success-message',
          result.data.message ||
            'Payment method update email sent successfully! Please check your email for the update link.',
          false,
          true
        );
        return;
      }

      if (result.data && result.data.method === 'email_failed') {
        throw new Error('Failed to send payment update email');
      }

      throw new Error('Unexpected response format from API');
    } catch (error) {
      console.error('Error updating payment method:', error);

      this.hideMessage('loading-message');

      this.showMessage(
        'error-message',
        'Unable to update payment method. Please try again or contact customer support.',
        true,
        false
      );
    }
  }

  /**
   * Create a payment method card element
   * @param {Object} paymentMethod - Payment method data
   * @param {boolean} isCurrent - Whether this is the current payment method
   * @returns {HTMLElement} The created card element
   */
  createPaymentMethodCard(paymentMethod, isCurrent = false) {
    // Get the template
    const template = this.querySelector('#payment-method-card-template');
    if (!template) {
      return document.createElement('div');
    }

    // Clone the template content
    const card = template.content.cloneNode(true).querySelector('.payment-method-card');

    // Set the current method class
    if (isCurrent) {
      card.classList.add('current-method');
    }

    // Set radio button properties
    const radio = card.querySelector('input[type="radio"]');
    const radioId = `payment-method-${paymentMethod.id.replace(/[^a-zA-Z0-9]/g, '')}`;
    radio.id = radioId;
    radio.value = paymentMethod.id;
    if (isCurrent) {
      radio.checked = true;
    }

    // Set label for attribute
    const label = card.querySelector('.payment-method-label');
    label.setAttribute('for', radioId);

    // Set payment method information
    card.querySelector('.card-brand').textContent = paymentMethod.brand;
    card.querySelector('.card-number').textContent = `•••• ${paymentMethod.last4}`;
    card.querySelector('.card-expiry').textContent = `Expires ${paymentMethod.expiryMonth}/${paymentMethod.expiryYear}`;

    // Show/hide badges
    if (isCurrent) {
      card.querySelector('.current-badge').style.display = 'inline-block';
    }
    if (paymentMethod.isDefault) {
      card.querySelector('.default-badge').style.display = 'inline-block';
    }

    // Set payment method ID on edit button
    const editButton = card.querySelector('.edit-payment-method-btn');
    editButton.setAttribute('data-payment-method-id', paymentMethod.id);

    // Add event listener for selection
    if (radio) {
      radio.addEventListener('change', () => {
        this.selectedPaymentMethodId = radio.value;
        this.updateButtonState();
      });
    }

    // Add event listener for edit button
    if (editButton) {
      editButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.editPaymentMethod(paymentMethod.id);
      });
    }

    return card;
  }

  /**
   * Load available payment methods from API
   */
  async loadPaymentMethods() {
    if (!this.customerId) {
      this.showMessage(
        'error-message',
        'Unable to load payment methods. Please ensure you are logged in or contact customer support.',
        true,
        false
      );
      return;
    }

    this.hideMessage('error-message');
    this.hideMessage('success-message');
    this.hideMessage('loading-message');

    this.showMessage('loading-message', 'Loading payment methods...', false, true);

    const paymentMethodsList = this.querySelector('#payment-methods-list');
    if (paymentMethodsList) paymentMethodsList.innerHTML = '';

    try {
      const response = await fetch(`${this.apiBaseUrl}/get_payment_methods?customerId=${this.customerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.data && result.data.availablePaymentMethods) {
        const container = this.querySelector('#payment-methods-list');
        if (!container) return;

        if (result.data.currentPaymentMethod) {
          const currentMethod = result.data.availablePaymentMethods.find(
            (method) => method.id === result.data.currentPaymentMethod.id
          );
          if (currentMethod) {
            container.appendChild(this.createPaymentMethodCard(currentMethod, true));
          }
        }

        result.data.availablePaymentMethods.forEach((paymentMethod) => {
          if (!result.data.currentPaymentMethod || paymentMethod.id !== result.data.currentPaymentMethod.id) {
            container.appendChild(this.createPaymentMethodCard(paymentMethod, false));
          }
        });

        if (result.data.availablePaymentMethods.length > 0) {
          if (result.data.currentPaymentMethod) {
            this.selectedPaymentMethodId = result.data.currentPaymentMethod.id;
            this.updateButtonState();
          }
        } else {
          this.showMessage(
            'error-message',
            'No payment methods found. Please add a payment method to your account first.',
            true,
            false
          );
        }
      } else {
        this.showMessage(
          'error-message',
          'Unable to retrieve payment methods. Please try again or contact customer support.',
          true,
          false
        );
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
      this.showMessage(
        'error-message',
        'Unable to load payment methods. Please check your connection and try again.',
        true,
        false
      );
    } finally {
      this.hideMessage('loading-message');
    }
  }

  /**
   * Update the selected payment method via API
   */
  async updatePaymentMethod() {
    if (!this.selectedPaymentMethodId) {
      this.showMessage('error-message', 'Please select a payment method to update.', true, false);
      return;
    }

    const currentPaymentMethod = this.querySelector('.payment-method-card.current-method input[type="radio"]');
    if (currentPaymentMethod && this.selectedPaymentMethodId === currentPaymentMethod.value) {
      this.showMessage(
        'error-message',
        'This payment method is already your current method. No update needed.',
        true,
        false
      );
      return;
    }

    this.hideMessage('success-message');
    this.hideMessage('error-message');
    this.hideMessage('loading-message');

    const button = this.querySelector('#update-payment-method-button');
    if (!button) return;

    const originalText = button.innerHTML;
    button.innerHTML = 'Updating...';
    button.disabled = true;

    try {
      let updateResults = [];

      if (this.nonGiftSubscriptions.length > 0) {
        for (const subscription of this.nonGiftSubscriptions) {
          const requestBody = {
            customerId: this.customerId,
            paymentMethodId: this.selectedPaymentMethodId,
            subscriptionContractId: subscription.subscriptionContractId,
          };

          const response = await fetch(`${this.apiBaseUrl}/update_subscription_payment_method`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            throw new Error(
              `HTTP error! status: ${response.status} for subscription ${subscription.subscriptionContractId}`
            );
          }

          const result = await response.json();
          if (result.data && result.data.status === 'updated') {
            updateResults.push({ id: subscription.subscriptionContractId, status: 'success' });
          } else {
            updateResults.push({ id: subscription.subscriptionContractId, status: 'failed' });
          }
        }

        const successfulUpdates = updateResults.filter((r) => r.status === 'success').length;
        const totalUpdates = updateResults.length;

        if (successfulUpdates === totalUpdates) {
          const message =
            totalUpdates === 1
              ? 'Payment method updated successfully! Your subscription will use the selected payment method.'
              : `Payment method updated successfully for all ${totalUpdates} subscriptions!`;

          this.showMessage('success-message', message);

          this.clearPaymentMethodInterface();
        } else {
          const failedCount = totalUpdates - successfulUpdates;
          throw new Error(`${failedCount} out of ${totalUpdates} subscription updates failed`);
        }
      } else {
        const requestBody = {
          customerId: this.customerId,
          paymentMethodId: this.selectedPaymentMethodId,
        };

        const response = await fetch(`${this.apiBaseUrl}/update_subscription_payment_method`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (result.data && result.data.status === 'updated') {
          this.showMessage('success-message', 'Payment method updated successfully at the customer level!');

          this.clearPaymentMethodInterface();
        } else {
          throw new Error('Customer-level update failed');
        }
      }
    } catch (error) {
      console.error('Error updating payment method:', error);
      this.showMessage(
        'error-message',
        'Unable to update payment method. Please try again or contact customer support.',
        true,
        false
      );
    } finally {
      button.innerHTML = originalText;
      button.disabled = false;
    }
  }
}

if (!customElements.get(NS)) {
  customElements.define(NS, PaymentMethodUpdate);
}
